import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Button } from '@/components/ui/button';
import { Download, Plus, Filter, X, TrendingUp } from 'lucide-react';

interface VariationRequest {
  id: string;
  jobId: string;
  property: string;
  provider: string;
  serviceType: string;
  original: string;
  requested: string;
  delta: string;
  submitted: string;
  status: 'Urgent' | 'Reviewing' | 'Submitted' | 'Info Requested';
}

const AdminVariationRequestsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<VariationRequest | null>(null);
  
  // Form states for approve modal
  const [approvedAmount, setApprovedAmount] = useState('');
  const [additionalDays, setAdditionalDays] = useState('');
  const [noteToProvider, setNoteToProvider] = useState('');
  
  // Form states for reject modal
  const [rejectionReason, setRejectionReason] = useState('');
  const [feedbackNote, setFeedbackNote] = useState('');
  const [loadingVariations, setLoadingVariations] = useState(true);
  const [variationRequests, setVariationRequests] = useState<VariationRequest[]>([]);
  const [variationMetrics, setVariationMetrics] = useState({ pending: 0, urgent: 0, approvedThisWeek: 0, rejectedThisWeek: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingVariations(true);
        const response = await apiService.request('/admin/variations', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const mapped: VariationRequest[] = response.data.map((v: any) => {
            const orig = v.original_amount || v.original_price || 0;
            const req = v.requested_amount || v.requested_price || 0;
            const delta = req - orig;
            return {
              id: v.id || v.variation_id,
              jobId: v.job_id || v.booking_id || '—',
              property: v.property_name || v.property_address || '—',
              provider: v.provider_name || '—',
              serviceType: (v.service_type || v.service_name || 'SERVICE').toUpperCase(),
              original: `$${Number(orig).toFixed(2)}`,
              requested: `$${Number(req).toFixed(2)}`,
              delta: `$${Number(delta).toFixed(2)}`,
              submitted: v.created_at ? new Date(v.created_at).toLocaleDateString('en-GB') : '—',
              status: v.status === 'urgent' ? 'Urgent' : v.status === 'reviewing' ? 'Reviewing' : v.status === 'info_requested' ? 'Info Requested' : 'Submitted',
            };
          });
          setVariationRequests(mapped);
          setVariationMetrics({
            pending: mapped.filter(v => v.status !== 'Approved' && v.status !== 'Rejected').length,
            urgent: mapped.filter(v => v.status === 'Urgent').length,
            approvedThisWeek: response.meta?.approved_this_week || 0,
            rejectedThisWeek: response.meta?.rejected_this_week || 0,
          });
        }
      } catch (error) {
        console.error('Error fetching variations:', error);
      } finally {
        setLoadingVariations(false);
      }
    };
    fetchData();
  }, []);

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'submitted', label: 'Submitted' },
    { id: 'under-review', label: 'Under Review' },
    { id: 'more-info', label: 'More Info Requested' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' }
  ];

  const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
    'Urgent': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-600' },
    'Reviewing': { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-600' },
    'Submitted': { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-600' },
    'Info Requested': { bg: 'bg-gray-50', text: 'text-gray-700', dot: 'bg-gray-600' }
  };

  const getServiceTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'MAINTENANCE': 'text-blue-600',
      'JANITORIAL': 'text-purple-600',
      'LANDSCAPING': 'text-green-600',
      'ELECTRICAL': 'text-orange-600',
      'SECURITY': 'text-teal-600'
    };
    return colors[type] || 'text-gray-600';
  };

  const handleOpenApproveModal = (variation: VariationRequest) => {
    setSelectedVariation(variation);
    setApprovedAmount(variation.requested.replace('$', '').replace(',', ''));
    setNoteToProvider('');
    setShowApproveModal(true);
  };

  const handleOpenRejectModal = (variation: VariationRequest) => {
    setSelectedVariation(variation);
    setRejectionReason('');
    setFeedbackNote('');
    setShowRejectModal(true);
  };

  // (audit 2026-06-09) — these confirm-modal handlers now delegate to the
  // real handleApproveVariation / handleRejectVariation defined below,
  // which call /admin/variations/:id/approve and /reject. Previously they
  // only console.log'd, so admin clicks did nothing.
  const handleConfirmApproval = async () => {
    await handleApproveVariation();
  };

  const handleConfirmReject = async () => {
    await handleRejectVariation();
  };

  // Handler: Approve variation request
  const handleApproveVariation = async () => {
    if (!selectedVariation) {
      alert('Please select a variation request to approve');
      return;
    }
    
    if (!approvedAmount || !additionalDays) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await apiService.request(`/admin/variations/${selectedVariation.id}/approve`, 'POST', {
        approvedAmount: parseFloat(approvedAmount.replace(/,/g, '')),
        additionalDays: parseInt(additionalDays),
        noteToProvider: noteToProvider,
        approvedBy: localStorage.getItem('adminId'),
        approvedAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert('✓ Variation request approved');
        setShowApproveModal(false);
        setSelectedVariation(null);
        setApprovedAmount('');
        setAdditionalDays('');
        setNoteToProvider('');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Approve variation error:', error);
      alert('Failed to approve variation: ' + error.message);
    }
  };

  // Handler: Reject variation request
  const handleRejectVariation = async () => {
    if (!selectedVariation) {
      alert('Please select a variation request to reject');
      return;
    }
    
    if (!rejectionReason) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      const response = await apiService.request(`/admin/variations/${selectedVariation.id}/reject`, 'POST', {
        reason: rejectionReason,
        feedbackNote: feedbackNote,
        rejectedBy: localStorage.getItem('adminId'),
        rejectedAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert('✓ Variation request rejected');
        setShowRejectModal(false);
        setSelectedVariation(null);
        setRejectionReason('');
        setFeedbackNote('');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Reject variation error:', error);
      alert('Failed to reject variation: ' + error.message);
    }
  };

  // Handler: Export variation requests
  const handleExportVariations = async () => {
    try {
      const response = await apiService.request('/admin/variations/export', 'GET', {
        tab: activeTab
      });
      
      if (response.success && response.data) {
        const csv = response.data;
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `variations-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Variations exported successfully');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Export variations error:', error);
      alert('Failed to export variations: ' + error.message);
    }
  };

  // Handler: Create new variation request
  const handleCreateVariation = async () => {
    const jobId = window.prompt('Enter Job ID:');
    if (!jobId) return;
    
    const amount = window.prompt('Enter requested amount:');
    if (!amount) return;

    try {
      const response = await apiService.request('/admin/variations/create', 'POST', {
        jobId: jobId,
        requestedAmount: parseFloat(amount),
        createdBy: localStorage.getItem('adminId'),
        createdAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert('✓ Variation request created');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Create variation error:', error);
      alert('Failed to create variation: ' + error.message);
    }
  };

  // Handler: Filter variations
  const handleFilterVariations = async () => {
    try {
      const filterInput = window.prompt('Enter filter criteria (status/provider/amount):');
      if (!filterInput) return;

      const response = await apiService.request('/admin/variations/filter', 'GET', {
        filter: filterInput
      });
      
      if (response.success) {
        alert(`✓ Found ${response.data?.count || 0} matching variations`);
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Filter variations error:', error);
      alert('Failed to filter variations: ' + error.message);
    }
  };

  // Handler: Export audit report
  const handleExportAuditReport = async () => {
    try {
      const response = await apiService.request('/admin/variations/audit-report', 'GET');
      
      if (response.success && response.data) {
        const csv = response.data;
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `variations-audit-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Audit report exported successfully');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Audit report error:', error);
      alert('Failed to export audit report: ' + error.message);
    }
  };

  const itemsPerPage = 5;
  const totalPages = Math.ceil(variationRequests.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const displayedRequests = variationRequests.slice(startIdx, startIdx + itemsPerPage);

  return (
    <>
      <Helmet>
        <title>Variation Requests - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Variation Requests</h1>
              <p className="text-sm text-gray-600">Marketplace scope and cost adjustment control centre</p>
            </div>
            <div className="flex items-center gap-3">
              <Button className="border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 font-bold text-sm px-4 py-2 flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm px-4 py-2 flex items-center gap-2 rounded-lg">
                <Plus className="w-4 h-4" />
                Create Variation
              </Button>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Pending Decision</div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{loadingVariations ? '—' : variationMetrics.pending}</div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="text-xs font-bold text-red-600 uppercase tracking-widest mb-3">Urgent Requests</div>
              <div className="text-4xl font-bold text-red-600 mb-2">{loadingVariations ? '—' : variationMetrics.urgent}</div>
              <div className="flex items-center gap-1 text-xs text-red-600">
                <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                <span>High Priority</span>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="text-xs font-bold text-green-600 uppercase tracking-widest mb-3">Approved This Week</div>
              <div className="text-4xl font-bold text-green-600 mb-2">{loadingVariations ? '—' : variationMetrics.approvedThisWeek}</div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Rejected This Week</div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{loadingVariations ? '—' : variationMetrics.rejectedThisWeek}</div>
            </div>
          </div>

          {/* Tabs and Controls */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCurrentPage(1);
                  }}
                  className={`px-5 py-3 font-medium text-sm transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button className="border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 font-bold text-sm px-4 py-2 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </Button>
              <Button className="border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 font-bold text-sm px-4 py-2">
                Export Audit Report
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-4 text-left">
                      <input type="checkbox" className="w-4 h-4 accent-teal-600 rounded cursor-pointer" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">VAR #</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Job ID</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Property</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Provider</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Service Type</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Original</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Requested</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Delta</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRequests.map((request, idx) => (
                    <tr 
                      key={request.id} 
                      onClick={() => navigate(`/admin/variations/${request.id}`)}
                      className={`border-b border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="w-4 h-4 accent-teal-600 rounded cursor-pointer" />
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-bold text-gray-900 text-sm">{request.id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-700 text-sm">{request.jobId}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-700 text-sm">{request.property}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-700 text-sm">{request.provider}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`font-bold text-sm ${getServiceTypeColor(request.serviceType)}`}>
                          {request.serviceType}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-700 text-sm font-medium">{request.original}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-900 font-bold text-sm">{request.requested}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-teal-600 font-bold text-sm">{request.delta}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenApproveModal(request);
                            }}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRejectModal(request);
                            }}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {startIdx + 1} to {Math.min(startIdx + itemsPerPage, variationRequests.length)} of {variationRequests.length} entries
              </span>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 text-sm">
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded font-medium text-sm transition-colors ${
                      currentPage === page
                        ? 'bg-teal-600 text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button className="px-3 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 text-sm">
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Approve Variation Modal */}
        {showApproveModal && selectedVariation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-bold text-teal-600 uppercase tracking-widest">Variation Workflow</p>
                  <h2 className="text-2xl font-bold text-gray-900">Approve Variation</h2>
                </div>
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Amount and Days Row */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-widest block mb-2">
                    Approved Amount (GBP)
                  </label>
                  <div className="bg-gray-100 rounded-lg px-4 py-3 flex items-center gap-2">
                    <span className="text-gray-600">$</span>
                    <input
                      type="text"
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(e.target.value)}
                      className="bg-transparent outline-none font-bold text-lg text-gray-900 w-full"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-widest block mb-2">
                    Additional Time (Days)
                  </label>
                  <div className="bg-gray-100 rounded-lg px-4 py-3">
                    <input
                      type="number"
                      value={additionalDays}
                      onChange={(e) => setAdditionalDays(e.target.value)}
                      className="bg-transparent outline-none font-bold text-lg text-gray-900 w-full"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Working Days</p>
                </div>
              </div>

              {/* Note to Provider */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-widest">Note to Provider</label>
                  <span className="text-xs font-bold text-teal-600 uppercase">Required</span>
                </div>
                <textarea
                  value={noteToProvider}
                  onChange={(e) => setNoteToProvider(e.target.value)}
                  placeholder="Add detailed notes for the provider..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 resize-none bg-gray-50"
                  rows={4}
                />
              </div>

              {/* Portfolio Variance Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-green-900 uppercase tracking-widest">Portfolio Variance</p>
                    <p className="text-sm font-bold text-green-900 mt-1">Impact on total budget: +0.82% Increase</p>
                  </div>
                  <span className="text-xs font-bold text-green-600 uppercase tracking-widest">Projected</span>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel Decision
                </button>
                <button
                  onClick={handleConfirmApproval}
                  className="flex-1 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg transition-colors"
                >
                  Confirm Approval
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Variation Modal */}
        {showRejectModal && selectedVariation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-bold text-red-600 uppercase tracking-widest">Variation Workflow</p>
                  <h2 className="text-2xl font-bold text-gray-900">Reject Variation</h2>
                </div>
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Rejection Reason */}
              <div className="mb-4">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-widest block mb-2">
                  Reason for Rejection
                </label>
                <select
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-600 bg-white"
                >
                  <option value="">Select a reason...</option>
                  <option value="exceeds-budget">Exceeds allocated budget</option>
                  <option value="insufficient-justification">Insufficient justification</option>
                  <option value="timeline-conflict">Timeline conflict with project schedule</option>
                  <option value="scope-change">Out of project scope</option>
                  <option value="quality-concerns">Quality concerns</option>
                </select>
              </div>

              {/* Feedback Note */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-widest">Feedback Note</label>
                  <span className="text-xs font-bold text-red-600 uppercase">Required</span>
                </div>
                <textarea
                  value={feedbackNote}
                  onChange={(e) => setFeedbackNote(e.target.value)}
                  placeholder="Provide feedback and next steps for the provider..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-600 resize-none bg-gray-50"
                  rows={4}
                />
              </div>

              {/* Risk Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">!</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-red-900 uppercase tracking-widest">Action Required</p>
                    <p className="text-sm text-red-900 mt-1">Provider will be notified and may resubmit revised variation</p>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Keep for Review
                </button>
                <button
                  onClick={handleConfirmReject}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </>
  );
};

export default AdminVariationRequestsPage;
